/**
 * notificationClosureStore — ONE answer to "is that thread closed?", shared by
 * the bell's three surfaces (2026-09-21, owner directive).
 *
 * WHY A MODULE-LEVEL STORE AND NOT A HOOK PER SURFACE. NotificationsBell,
 * MobileNotifications and /notifications all render the same feed and all ask
 * the same question about the same thread ids. A hook each would mean three
 * caches, three sets of reads, and three chances to disagree about one row —
 * so the cache is here, once, and the React binding is a thin adapter over it.
 *
 * WHY THE LADDER IS SHORTER THAN THE CHAT'S. useMentionThreadClosure has four
 * tiers because the chat host owns a live index and a workspace-scoped archived
 * listener. Out here neither is available or wanted:
 *
 *   · there is no live index to compare against, so the free "present ⇒ open"
 *     tier does not exist;
 *   · the archived listener is scoped to (instanceId, accountId) while a bell
 *     row may name a thread in ANOTHER workspace — that is what its `ws` field
 *     is for. The single-doc probe reads users/{uid}/chat_threads/{threadId},
 *     which is uid-keyed and therefore answers across workspaces; the listener
 *     cannot;
 *   · the bell mounts on every marketing page a signed-in person opens, and a
 *     200-document listener attached there to answer a question about at most
 *     ten rows is a cost nobody asked for.
 *
 * So: the durable tombstone (synchronous, free, cross-tab) and then one bounded
 * cached probe. Callers resolve on DEMAND — while the dropdown is open, or for
 * the ten rows /notifications is showing — so every other page costs nothing.
 *
 * EVERY FAILURE FALLS TOWARD "OPEN", the same direction as the chat's ladder
 * and for the same reason: the archive fanout skips a member whose index doc
 * does not exist yet, so a fail-closed reading would grey a freshly-invited
 * member's row while their thread is perfectly live. A thrown read is not an
 * answer — it is re-asked, up to a bound, and renders open meanwhile.
 *
 * Pure and Firebase-free: the readers are injected, which is what lets the
 * whole of this be exercised with two plain functions and no emulator.
 */
import {
  MAX_CLOSURE_PROBE_ATTEMPTS,
  planClosureProbes,
  type ThreadClosureFacts,
} from './threadClosure';

/** The two reads this store knows how to make. Injected — see the header. */
export interface NotificationClosureReaders {
  /** Zero-cost, synchronous: the durable archive tombstone this browser holds. */
  isLocallyArchived: (threadId: string) => boolean;
  /** One single-doc read of the viewer's own thread index doc. */
  readArchiveInfo: (threadId: string) => Promise<ThreadClosureFacts>;
}

type Listener = () => void;

/** Stable identity for "nothing known", so a consumer never re-renders on it. */
const EMPTY: ReadonlyMap<string, ThreadClosureFacts> = Object.freeze(
  new Map<string, ThreadClosureFacts>(),
) as ReadonlyMap<string, ThreadClosureFacts>;

/** The ladder's "live index" input, which this store deliberately does not have. */
const EMPTY_IDS: ReadonlySet<string> = Object.freeze(new Set<string>()) as ReadonlySet<string>;

let ownerUid: string | null = null;
let facts: ReadonlyMap<string, ThreadClosureFacts> = EMPTY;
const mutable = new Map<string, ThreadClosureFacts>();
/** Ids a probe answered NEGATIVELY, or gave up on — stop asking. */
const answered = new Set<string>();
const inFlight = new Set<string>();
const failures = new Map<string, number>();
const listeners = new Set<Listener>();

function emit(): void {
  // The snapshot is taken BEFORE the loop: a listener is free to unsubscribe
  // (or subscribe) while being notified, and iterating the live set while it
  // mutates is how threadArchiveNotice's own emit() came to hand `null` to
  // every subscriber after the first.
  for (const listener of Array.from(listeners)) {
    try {
      listener();
    } catch { /* one bad listener must not mute the rest */ }
  }
}

function clearAll(): void {
  mutable.clear();
  answered.clear();
  inFlight.clear();
  failures.clear();
  facts = EMPTY;
}

/**
 * The current answers. Referentially stable between changes, which is what
 * useSyncExternalStore requires of a snapshot.
 */
export function getNotificationClosures(): ReadonlyMap<string, ThreadClosureFacts> {
  return facts;
}

export function subscribeNotificationClosures(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/**
 * Point the store at a person. A DIFFERENT uid wipes everything — one person's
 * answers about their own threads must never be shown to the next person to
 * sign in on this browser, and a stale positive would grey a row that is not
 * even theirs. Signing out (null) clears for the same reason.
 */
export function setNotificationClosureOwner(uid: string | null): void {
  const next = (uid || '').trim() || null;
  if (next === ownerUid) return;
  ownerUid = next;
  clearAll();
  emit();
}

/** Test seam only: forget everything, including the owner. */
export function resetNotificationClosuresForTests(): void {
  ownerUid = null;
  clearAll();
  listeners.clear();
}

/**
 * Latch one thread's facts. ONE-WAY, exactly like archiving itself: a later
 * negative never clears a positive, because a positive can only have come from
 * a tombstone or a document that said so, while a negative can be a read that
 * raced an archive write still in flight.
 */
function latch(threadId: string, next: ThreadClosureFacts): boolean {
  const id = String(threadId || '').trim();
  if (!id) return false;
  const current = mutable.get(id);
  if (current?.archived === true && next.archived !== true) return false;
  if (current
    && current.archived === next.archived
    && current.archivedBy === next.archivedBy
    && current.archivedByName === next.archivedByName
    && current.atMs === next.atMs) return false;
  mutable.set(id, next);
  facts = new Map(mutable);
  return true;
}

/**
 * Forget every NEGATIVE answer, keeping the positives.
 *
 * "Keeping" means keeping them ANSWERED, not merely latched: a thread that is
 * closed is closed for good (archiving is one-way), so re-asking about it on
 * every panel open would spend a read per closed row forever — and for the
 * commonest closed shape there is, a removal or a personal archive, the
 * document carries no attribution, so the second read would learn nothing the
 * first did not.
 */
export function clearNotificationClosureNegatives(): void {
  for (const id of Array.from(answered)) {
    if (mutable.get(id)?.archived === true) continue;
    answered.delete(id);
  }
  failures.clear();
}

/**
 * Most probe passes one resolve call will run. A safety bound only: each pass
 * takes up to CLOSURE_PROBE_CAP ids, and the loop stops the moment nothing is
 * left to ask about, so this is reached only by a pathologically large feed.
 */
const MAX_PROBE_PASSES = 12;

/**
 * Resolve whatever is not yet known about these threads.
 *
 * THE TOMBSTONE DECIDES RETIREMENT; THE PROBE STILL RUNS, FOR THE WORDS. This
 * is the one place this store deliberately diverges from the chat's ladder,
 * and it is what makes the owner's actual ask deliverable. A tombstone proves
 * a thread is closed and carries NO attribution — addArchivedTombstone is also
 * called by the passing-notice probe and the stale-draft tidy, so it does not
 * even prove a self-archive. The chat can afford to stop there because its
 * archived-index listener supplies the name in parallel; out here there is no
 * listener, so stopping at the tombstone would render the weakest sentence
 * there is ("this thread is closed to you now") for every row — never "Miles
 * archived this thread on sep 21", which is precisely what was asked for. So
 * the tombstone latches the safe half at once, and the probe upgrades it:
 * `latch` blocks only positive→negative, so unknown→person is allowed through.
 *
 * IT DRAINS. planClosureProbes caps a pass at CLOSURE_PROBE_CAP, and in the
 * chat that cap is a belt because tier 1 removes nearly every id before it is
 * consulted. Here there is no tier 1, so an un-drained cap would BE the
 * mechanism: the first twelve rows would resolve and the rest would stay
 * clickable for the life of the tab — the exact dead end this exists to close.
 * Every probed id is marked answered, positives included (archiving is
 * one-way, so there is nothing more to learn), which is what guarantees the
 * loop terminates.
 */
export async function resolveNotificationClosures(
  threadIds: readonly string[],
  readers: NotificationClosureReaders,
): Promise<void> {
  if (!ownerUid) return;
  const uidAtStart = ownerUid;
  let changed = false;

  for (const raw of threadIds) {
    const id = String(raw || '').trim();
    if (!id || mutable.get(id)?.archived === true) continue;
    if (readers.isLocallyArchived(id)) {
      if (latch(id, { archived: true, atMs: 0 })) changed = true;
    }
  }
  if (changed) emit();

  for (let pass = 0; pass < MAX_PROBE_PASSES; pass += 1) {
    if (ownerUid !== uidAtStart) return;
    // `tombstoned` is deliberately NOT passed: see the header — a tombstoned
    // thread still needs its attribution read.
    const plan = planClosureProbes({
      threadIds,
      liveThreadIds: EMPTY_IDS,
      answered,
      inFlight,
    });
    const pending = plan.threadIds.filter((id) => {
      const known = mutable.get(id);
      if (!known) return true;
      // Latched by the tombstone, with nothing to say about who: ask once.
      return known.archived === true && !known.archivedBy;
    });
    if (pending.length === 0) return;

    for (const id of pending) inFlight.add(id);
    let latched = false;
    await Promise.all(pending.map(async (id) => {
      try {
        const info = await readers.readArchiveInfo(id);
        // The person changed while this was in flight: the answer is about
        // somebody else's thread list now. Drop it.
        if (ownerUid !== uidAtStart) return;
        if (info.archived === true && latch(id, info)) latched = true;
        // Answered either way. A doc that says "not archived", or no doc at
        // all, renders OPEN — the safe direction (see the header) — and a
        // positive has nothing left to tell us, because archiving is one-way.
        answered.add(id);
      } catch {
        if (ownerUid !== uidAtStart) return;
        const attempts = (failures.get(id) || 0) + 1;
        failures.set(id, attempts);
        // Bounded: a permanently failing read degrades to "renders open"
        // rather than being asked forever by every re-open of the panel.
        if (attempts >= MAX_CLOSURE_PROBE_ATTEMPTS) answered.add(id);
      } finally {
        inFlight.delete(id);
      }
    }));
    if (latched) emit();
    // A pass in which every id threw leaves them unanswered and re-plannable;
    // stopping here is what keeps a dead network from spinning this loop.
    if (!latched && !pending.some((id) => answered.has(id))) return;
  }
}
